/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useLazyQuery, useMutation } from '@apollo/client'
import React from 'react'

import {
  ADD_BOUNDING_BOX_TO_ANNOTATED_IMAGE,
  AddBoundingBoxToAnnotatedImageMutation,
  AddBoundingBoxToAnnotatedImageMutationVariables,
  ADD_TAG,
  AddTagMutation,
  AddTagMutationVariables,
  AnnotatedImage,
  REMOVE_BOUNDING_BOX_FROM_ANNOTATED_IMAGE,
  RemoveBoundingBoxFromAnnotatedImageMutation,
  RemoveBoundingBoxFromAnnotatedImageMutationVariables,
  SEARCH_TAGS,
  SearchTagsQuery,
  SearchTagsQueryVariables,
  UPDATE_BOUNDING_BOX,
  UpdateBoundingBoxMutation,
  UpdateBoundingBoxMutationVariables,
  VERIFY_IMAGE_DETECTIONS,
  VerifyImageDetectionsMutation,
  VerifyImageDetectionsMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

import {
  DetectionBox,
  DetectionClass,
  DetectionTagOption,
  DETECTION_CLASS_ORDER,
  getOperationErrorMessage,
  INITIAL_DETECTION_TAGS,
  isRemoveBoundingBoxPayload,
  isSyntheticTagId,
  isUpdateBoundingBoxResult,
  mergeTagOptions,
  toDetectionBox,
  toDetectionTagId,
  toMetaClass,
} from './detectionUtils'

const isPersistedBoundingBoxId = (boxId: string) =>
  !boxId.startsWith('pending-')

type UseDetectionEditorArgs = {
  image: Pick<AnnotatedImage, 'id' | 'imageUrl'> | null
  canEdit: boolean
  onApproveSuccess: () => Promise<unknown> | unknown
  markImageHandled: (imageId: string) => void
  unmarkImageHandled: (imageId: string) => void
}

export function useDetectionEditor({
  image,
  canEdit,
  onApproveSuccess,
  markImageHandled,
  unmarkImageHandled,
}: UseDetectionEditorArgs) {
  const { t } = useT()
  const [activeClass, setActiveClass] =
    React.useState<DetectionClass>('PRODUCT')
  const [activeTag, setActiveTag] = React.useState<DetectionTagOption>(
    INITIAL_DETECTION_TAGS[0],
  )
  const [availableTags, setAvailableTags] = React.useState<
    DetectionTagOption[]
  >(INITIAL_DETECTION_TAGS)
  const [tagSearch, setTagSearch] = React.useState('')
  const [selectedBoxIds, setSelectedBoxIds] = React.useState<string[]>([])
  const [hoveredClass, setHoveredClass] = React.useState<DetectionClass | null>(
    null,
  )
  const [hiddenClasses, setHiddenClasses] = React.useState<Set<DetectionClass>>(
    new Set(),
  )
  const [boxes, setBoxes] = React.useState<DetectionBox[]>([])
  const [snackbarMessage, setSnackbarMessage] = React.useState('')

  const [searchTags, { loading: searchingTags }] = useLazyQuery<
    SearchTagsQuery,
    SearchTagsQueryVariables
  >(SEARCH_TAGS, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
    onCompleted: queryData => {
      if (!queryData.searchTags.length) return
      setAvailableTags(current =>
        mergeTagOptions([...current, ...queryData.searchTags]),
      )
    },
  })

  const [addTag, { loading: addingTag }] = useMutation<
    AddTagMutation,
    AddTagMutationVariables
  >(ADD_TAG, {
    context: { endPoint: 'mlUltra' },
  })

  const [addBoundingBox] = useMutation<
    AddBoundingBoxToAnnotatedImageMutation,
    AddBoundingBoxToAnnotatedImageMutationVariables
  >(ADD_BOUNDING_BOX_TO_ANNOTATED_IMAGE, {
    context: { endPoint: 'mlUltra' },
  })

  const [updateBoundingBox] = useMutation<
    UpdateBoundingBoxMutation,
    UpdateBoundingBoxMutationVariables
  >(UPDATE_BOUNDING_BOX, {
    context: { endPoint: 'mlUltra' },
  })

  const [removeBoundingBox, { loading: removingBoxes }] = useMutation<
    RemoveBoundingBoxFromAnnotatedImageMutation,
    RemoveBoundingBoxFromAnnotatedImageMutationVariables
  >(REMOVE_BOUNDING_BOX_FROM_ANNOTATED_IMAGE, {
    context: { endPoint: 'mlUltra' },
  })

  const [verifyImageDetections, { loading: verifying }] = useMutation<
    VerifyImageDetectionsMutation,
    VerifyImageDetectionsMutationVariables
  >(VERIFY_IMAGE_DETECTIONS, {
    context: { endPoint: 'mlUltra' },
  })

  React.useEffect(() => {
    const matchingTag = availableTags.find(
      tag =>
        tag.name.toLowerCase() === activeTag.name.toLowerCase() &&
        !isSyntheticTagId(tag.id),
    )
    if (matchingTag && matchingTag.id !== activeTag.id) {
      setActiveTag(matchingTag)
    }
  }, [activeTag.id, activeTag.name, availableTags])

  React.useEffect(() => {
    const query = tagSearch.trim()
    const timeoutId = window.setTimeout(() => {
      void searchTags({ variables: { query } })
    }, 200)
    return () => window.clearTimeout(timeoutId)
  }, [searchTags, tagSearch])

  React.useEffect(() => {
    void searchTags({ variables: { query: '' } })
  }, [searchTags])

  const mergeDiscoveredTags = React.useCallback((nextBoxes: DetectionBox[]) => {
    const discoveredTags = nextBoxes.flatMap(box =>
      box.tag
        ? [
            {
              id: box.tagId || toDetectionTagId(box.tag),
              name: box.tag,
            },
          ]
        : [],
    )
    if (!discoveredTags.length) return
    setAvailableTags(current =>
      mergeTagOptions([...current, ...discoveredTags]),
    )
  }, [])

  const handleIncomingImage = React.useCallback((nextBoxes: DetectionBox[]) => {
    setBoxes(nextBoxes)
    setSelectedBoxIds([])
  }, [])

  const selectedBoxes = boxes.filter(box => selectedBoxIds.includes(box.id))
  const selectedProductBoxes = selectedBoxes.filter(
    box => box.detectionClass === 'PRODUCT',
  )

  const counts = React.useMemo(
    () =>
      DETECTION_CLASS_ORDER.reduce(
        (acc, detectionClass) => ({
          ...acc,
          [detectionClass]: boxes.filter(
            box => box.detectionClass === detectionClass,
          ).length,
        }),
        {} as Record<DetectionClass, number>,
      ),
    [boxes],
  )

  const handleToggleClassVisibility = (detectionClass: DetectionClass) => {
    setHiddenClasses(current => {
      const next = new Set(current)
      if (next.has(detectionClass)) next.delete(detectionClass)
      else next.add(detectionClass)
      return next
    })
  }

  const handleSelectBoxesForClass = React.useCallback(
    (
      detectionClass: DetectionClass,
      mode: 'select' | 'deselect' = 'select',
    ) => {
      if (mode === 'deselect') {
        const boxIdsForClass = new Set(
          boxes
            .filter(box => box.detectionClass === detectionClass)
            .map(box => box.id),
        )
        setSelectedBoxIds(current =>
          current.filter(boxId => !boxIdsForClass.has(boxId)),
        )
        return
      }

      setSelectedBoxIds(
        boxes
          .filter(box => box.detectionClass === detectionClass)
          .map(box => box.id),
      )
    },
    [boxes],
  )

  const ensurePersistedTag = React.useCallback(
    async (tag: DetectionTagOption) => {
      if (!isSyntheticTagId(tag.id)) return tag

      const existingTag = availableTags.find(
        option =>
          option.name.toLowerCase() === tag.name.toLowerCase() &&
          !isSyntheticTagId(option.id),
      )
      if (existingTag) {
        setActiveTag(current => (current.id === tag.id ? existingTag : current))
        return existingTag
      }

      const response = await addTag({ variables: { name: tag.name } })
      const nextTag = response.data?.addTag
      if (!nextTag) {
        throw new Error(
          t('detectionEditor.errors.createTag', 'Failed to create tag'),
        )
      }

      setAvailableTags(current => mergeTagOptions([...current, nextTag]))
      setActiveTag(current => (current.id === tag.id ? nextTag : current))
      return nextTag
    },
    [addTag, availableTags],
  )

  const handleCreateBox = async (
    bounds: Pick<DetectionBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  ) => {
    if (!image || !canEdit) return
    let persistedTag = activeTag
    try {
      if (activeClass === 'PRODUCT') {
        persistedTag = await ensurePersistedTag(activeTag)
      }
    } catch (tagError) {
      console.error('Failed to ensure product tag', tagError)
      setSnackbarMessage(
        t(
          'detectionEditor.errors.createProductTag',
          'Failed to create product tag',
        ),
      )
      return
    }
    const optimisticId = `pending-${crypto.randomUUID()}`
    const optimisticBox: DetectionBox = {
      id: optimisticId,
      sourceImageUrl: image.imageUrl,
      ...bounds,
      detectionClass: activeClass,
      tagId: activeClass === 'PRODUCT' ? persistedTag.id : null,
      tag: activeClass === 'PRODUCT' ? persistedTag.name : null,
    }
    setBoxes(current => [...current, optimisticBox])
    try {
      const response = await addBoundingBox({
        variables: {
          imageId: image.id,
          ...bounds,
          metaClass: toMetaClass(activeClass, persistedTag.name),
          tagId: activeClass === 'PRODUCT' ? persistedTag.id : null,
        },
      })
      const payload = response.data?.addBoundingBoxToAnnotatedImage
      if (!payload) {
        setBoxes(current => current.filter(box => box.id !== optimisticId))
        return
      }
      if (payload.__typename === 'OperationInfo') {
        setBoxes(current => current.filter(box => box.id !== optimisticId))
        setSnackbarMessage(getOperationErrorMessage(t, payload.messages))
        return
      }
      const nextBox = toDetectionBox(payload.boundingBox)
      setBoxes(current =>
        current.map(box => (box.id === optimisticId ? nextBox : box)),
      )
      mergeDiscoveredTags([nextBox])
    } catch (createError) {
      setBoxes(current => current.filter(box => box.id !== optimisticId))
      console.error('Failed to create bounding box', createError)
      setSnackbarMessage(
        t('detectionEditor.errors.createBox', 'Failed to create box'),
      )
    }
  }

  const handleApplyActiveTag = async () => {
    if (!selectedProductBoxes.length || !canEdit) return
    let persistedTag = activeTag
    try {
      persistedTag = await ensurePersistedTag(activeTag)
    } catch (tagError) {
      console.error('Failed to ensure product tag', tagError)
      setSnackbarMessage(
        t(
          'detectionEditor.errors.createProductTag',
          'Failed to create product tag',
        ),
      )
      return
    }
    const previousBoxesById = new Map(
      selectedProductBoxes.map(box => [box.id, box]),
    )
    setBoxes(current =>
      current.map(box =>
        previousBoxesById.has(box.id)
          ? {
              ...box,
              tagId: persistedTag.id,
              tag: persistedTag.name,
            }
          : box,
      ),
    )
    try {
      const results = await Promise.all(
        selectedProductBoxes
          .filter(box => box.tagId !== persistedTag.id)
          .map(box =>
            updateBoundingBox({
              variables: {
                boxId: box.id,
                metaClass: toMetaClass(box.detectionClass, persistedTag.name),
                tagId: persistedTag.id,
              },
            }),
          ),
      )

      const updatedBoxes = results
        .map(result => result.data?.updateBoundingBox)
        .filter(isUpdateBoundingBoxResult)
        .map(payload => toDetectionBox(payload.boundingBox))

      const errorPayload = results
        .map(result => result.data?.updateBoundingBox)
        .find(payload => payload?.__typename === 'OperationInfo')
      if (errorPayload?.__typename === 'OperationInfo') {
        setBoxes(current =>
          current.map(box => previousBoxesById.get(box.id) || box),
        )
        setSnackbarMessage(getOperationErrorMessage(t, errorPayload.messages))
        return
      }

      if (!updatedBoxes.length) return
      setBoxes(current =>
        current.map(
          box => updatedBoxes.find(next => next.id === box.id) || box,
        ),
      )
    } catch (updateError) {
      setBoxes(current =>
        current.map(box => previousBoxesById.get(box.id) || box),
      )
      console.error('Failed to update box tag', updateError)
      setSnackbarMessage(
        t(
          'detectionEditor.errors.updateSelectedBoxes',
          'Failed to update selected boxes',
        ),
      )
    }
  }

  const handleApplyCurrentLabel = async () => {
    if (!selectedBoxIds.length || !canEdit) return
    let persistedTag = activeTag
    try {
      if (activeClass === 'PRODUCT') {
        persistedTag = await ensurePersistedTag(activeTag)
      }
    } catch (tagError) {
      console.error('Failed to ensure product tag', tagError)
      setSnackbarMessage(
        t(
          'detectionEditor.errors.createProductTag',
          'Failed to create product tag',
        ),
      )
      return
    }
    const previousBoxesById = new Map(selectedBoxes.map(box => [box.id, box]))
    setBoxes(current =>
      current.map(box =>
        previousBoxesById.has(box.id)
          ? {
              ...box,
              detectionClass: activeClass,
              tagId: activeClass === 'PRODUCT' ? persistedTag.id : null,
              tag: activeClass === 'PRODUCT' ? persistedTag.name : null,
            }
          : box,
      ),
    )
    try {
      const results = await Promise.all(
        selectedBoxes
          .filter(box => {
            const nextTagId = activeClass === 'PRODUCT' ? persistedTag.id : null
            const nextMetaClass = toMetaClass(activeClass, persistedTag.name)
            return (
              box.detectionClass !== activeClass ||
              box.tagId !== nextTagId ||
              toMetaClass(box.detectionClass, box.tag) !== nextMetaClass
            )
          })
          .map(box =>
            updateBoundingBox({
              variables: {
                boxId: box.id,
                metaClass: toMetaClass(activeClass, persistedTag.name),
                tagId: activeClass === 'PRODUCT' ? persistedTag.id : null,
              },
            }),
          ),
      )

      const updatedBoxes = results
        .map(result => result.data?.updateBoundingBox)
        .filter(isUpdateBoundingBoxResult)
        .map(payload => toDetectionBox(payload.boundingBox))

      const errorPayload = results
        .map(result => result.data?.updateBoundingBox)
        .find(payload => payload?.__typename === 'OperationInfo')
      if (errorPayload?.__typename === 'OperationInfo') {
        setBoxes(current =>
          current.map(box => previousBoxesById.get(box.id) || box),
        )
        setSnackbarMessage(getOperationErrorMessage(t, errorPayload.messages))
        return
      }

      if (!updatedBoxes.length) return
      setBoxes(current =>
        current.map(
          box => updatedBoxes.find(next => next.id === box.id) || box,
        ),
      )
    } catch (updateError) {
      setBoxes(current =>
        current.map(box => previousBoxesById.get(box.id) || box),
      )
      console.error('Failed to relabel boxes', updateError)
      setSnackbarMessage(
        t(
          'detectionEditor.errors.updateSelectedBoxes',
          'Failed to update selected boxes',
        ),
      )
    }
  }

  const handleDeleteSelectedBox = async () => {
    if (!image || !selectedBoxIds.length || !canEdit) return
    const selectedIdsSet = new Set(selectedBoxIds)
    const previousBoxes = boxes.filter(box => selectedIdsSet.has(box.id))
    const previousPersisted = previousBoxes.filter(box =>
      isPersistedBoundingBoxId(box.id),
    )
    const persistedBoxIds = selectedBoxIds.filter(isPersistedBoundingBoxId)
    setBoxes(current => current.filter(box => !selectedIdsSet.has(box.id)))
    setSelectedBoxIds([])
    if (!persistedBoxIds.length) return
    try {
      const results = await Promise.all(
        persistedBoxIds.map(boxId =>
          removeBoundingBox({
            variables: {
              imageId: image.id,
              boxId,
            },
          }),
        ),
      )

      const removedIds = results
        .map(result => result.data?.removeBoundingBoxFromAnnotatedImage)
        .filter(
          (
            payload,
          ): payload is Extract<
            RemoveBoundingBoxFromAnnotatedImageMutation['removeBoundingBoxFromAnnotatedImage'],
            { __typename: 'RemoveBoundingBoxResult' }
          > => isRemoveBoundingBoxPayload(payload) && payload.success,
        )
        .map(payload => payload.boundingBox.id)

      const errorPayload = results
        .map(result => result.data?.removeBoundingBoxFromAnnotatedImage)
        .find(payload => payload?.__typename === 'OperationInfo')
      if (errorPayload?.__typename === 'OperationInfo') {
        const removedIdsSet = new Set(removedIds)
        const failedBoxes = previousPersisted.filter(
          box => !removedIdsSet.has(box.id),
        )
        if (failedBoxes.length) {
          setBoxes(current => [...current, ...failedBoxes])
        }
        setSnackbarMessage(getOperationErrorMessage(t, errorPayload.messages))
        return
      }

      if (!removedIds.length) return
      if (removedIds.length !== previousPersisted.length) {
        const removedIdsSet = new Set(removedIds)
        const failedRestores = previousPersisted.filter(
          box => !removedIdsSet.has(box.id),
        )
        if (failedRestores.length) {
          setBoxes(current => [...current, ...failedRestores])
        }
      }
    } catch (removeError) {
      setBoxes(current => [...current, ...previousBoxes])
      console.error('Failed to remove boxes', removeError)
      setSnackbarMessage(
        t(
          'detectionEditor.errors.deleteSelectedBoxes',
          'Failed to delete selected boxes',
        ),
      )
    }
  }

  const allProductBoxesTagged = boxes
    .filter(box => box.detectionClass === 'PRODUCT')
    .every(box => Boolean(box.tagId))

  const canApplyActiveTag =
    activeClass === 'PRODUCT' &&
    selectedProductBoxes.some(box => box.tagId !== activeTag.id)
  const canApplyCurrentLabel = selectedBoxes.some(
    box => box.detectionClass !== activeClass,
  )

  const filteredTags = React.useMemo(() => {
    const normalizedSearch = tagSearch.trim().toLowerCase()
    return availableTags.filter(tag =>
      normalizedSearch
        ? tag.name.toLowerCase().includes(normalizedSearch)
        : true,
    )
  }, [availableTags, tagSearch])

  const canUseSearchAsTag =
    tagSearch.trim().length > 0 &&
    !availableTags.some(
      tag => tag.name.toLowerCase() === tagSearch.trim().toLowerCase(),
    )

  const showActiveTagSection =
    activeClass === 'PRODUCT' || selectedProductBoxes.length > 0

  const handleCreateTag = async () => {
    const name = tagSearch.trim()
    if (!name) return
    try {
      const response = await addTag({ variables: { name } })
      const nextTag = response.data?.addTag
      if (!nextTag) {
        setSnackbarMessage(
          t(
            'detectionEditor.errors.createProductTag',
            'Failed to create product tag',
          ),
        )
        return
      }
      setAvailableTags(current => mergeTagOptions([...current, nextTag]))
      setActiveTag(nextTag)
      setTagSearch('')
    } catch (createTagError) {
      console.error('Failed to create tag', createTagError)
      setSnackbarMessage(
        t(
          'detectionEditor.errors.createProductTag',
          'Failed to create product tag',
        ),
      )
    }
  }

  const handleApprove = async () => {
    if (!image || !canEdit || !allProductBoxesTagged) return
    markImageHandled(image.id)
    try {
      const response = await verifyImageDetections({
        variables: { imageId: image.id },
      })
      const payload = response.data?.verifyImageDetections
      if (
        payload?.__typename !== 'VerifyImageDetectionsResult' ||
        !payload.success
      ) {
        unmarkImageHandled(image.id)
        setSnackbarMessage(
          t('detectionEditor.errors.approveImage', 'Failed to approve image'),
        )
        return
      }
      setSelectedBoxIds([])
      setSnackbarMessage(
        t('detectionEditor.approvedImage', {
          imageId: image.id.slice(0, 8),
          defaultValue: `Approved image ${image.id.slice(0, 8)}`,
        }),
      )
      await onApproveSuccess()
    } catch (approveError) {
      unmarkImageHandled(image.id)
      console.error('Failed to verify image detections', approveError)
      setSnackbarMessage(
        t('detectionEditor.errors.approveImage', 'Failed to approve image'),
      )
    }
  }

  return {
    activeClass,
    activeTag,
    addingTag,
    boxes,
    canApplyActiveTag,
    canApplyCurrentLabel,
    canUseSearchAsTag,
    counts,
    filteredTags,
    handleApplyActiveTag,
    handleApplyCurrentLabel,
    handleApprove,
    handleCreateBox,
    handleCreateTag,
    handleDeleteSelectedBox,
    handleIncomingImage,
    handleSelectBoxesForClass,
    handleToggleClassVisibility,
    hiddenClasses,
    hoveredClass,
    mergeDiscoveredTags,
    removingBoxes,
    searchingTags,
    selectedBoxIds,
    selectedBoxes,
    setActiveClass,
    setActiveTag,
    setHoveredClass,
    setSelectedBoxIds,
    setSnackbarMessage,
    setTagSearch,
    showActiveTagSection,
    snackbarMessage,
    tagSearch,
    verifying,
  }
}
